import {useMemo, useState} from 'react'
import {Form, Modal} from 'react-bootstrap'
import Select from 'react-select'
import {PhoneInput} from '../../../components/PhoneInput'
import {useMutation, useQuery} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {GET_ONBOARDING_UPLINE_CANDIDATES} from '../../../gql/queries/agentOnboardingQueries'
import {CREATE_AGENT_ONBOARDING_APPLICATION} from '../../../gql/mutations/agentOnboardingMutations'
import {IUplineCandidate, ReviewStatus} from '../../../types/agentOnboarding'
import {US_STATES} from '../../../helpers/catalogs'

const M = defineMessages({
  title: {id: 'onboarding.create.title', defaultMessage: 'Add agent manually'},
  hint: {
    id: 'onboarding.create.hint',
    defaultMessage:
      'For agents who never went through the portal. Licensing, documents and carriers are completed afterwards from the application detail.',
  },
  fullName: {id: 'onboarding.create.fullName', defaultMessage: 'Full name'},
  fullNamePh: {id: 'onboarding.create.fullNamePh', defaultMessage: 'Agent name'},
  email: {id: 'onboarding.create.email', defaultMessage: 'Email'},
  emailPh: {id: 'onboarding.create.emailPh', defaultMessage: 'email@domain.com'},
  phone: {id: 'onboarding.create.phone', defaultMessage: 'Phone'},
  optional: {id: 'onboarding.create.optional', defaultMessage: 'Optional'},
  address: {id: 'onboarding.create.address', defaultMessage: 'Address'},
  dob: {id: 'onboarding.create.dob', defaultMessage: 'Date of birth'},
  state: {id: 'onboarding.create.state', defaultMessage: 'State'},
  statePh: {id: 'onboarding.create.statePh', defaultMessage: 'Select'},
  upline: {id: 'onboarding.create.upline', defaultMessage: 'Upline (reports to)'},
  noUpline: {id: 'onboarding.create.noUpline', defaultMessage: 'No upline'},
  status: {id: 'onboarding.create.status', defaultMessage: 'Application status'},
  commission: {id: 'onboarding.create.commission', defaultMessage: 'Commission (%)'},
  noCommission: {id: 'onboarding.create.noCommission', defaultMessage: 'Not set'},
  cancel: {id: 'onboarding.create.cancel', defaultMessage: 'Cancel'},
  submit: {id: 'onboarding.create.submit', defaultMessage: 'Add agent'},
  created: {id: 'onboarding.create.created', defaultMessage: 'Agent added'},
  failed: {id: 'onboarding.create.failed', defaultMessage: 'Could not add the agent'},
  invalidEmail: {id: 'onboarding.info.invalidEmail', defaultMessage: 'Enter a valid email address'},
  reviewPENDING: {id: 'onboarding.review.PENDING', defaultMessage: 'Pending'},
  reviewREVIEWING: {id: 'onboarding.review.REVIEWING', defaultMessage: 'Reviewing'},
  reviewAPPROVED: {id: 'onboarding.review.APPROVED', defaultMessage: 'Approved'},
  reviewREJECTED: {id: 'onboarding.review.REJECTED', defaultMessage: 'Rejected'},
  reviewACTIVATED: {id: 'onboarding.review.ACTIVATED', defaultMessage: 'Activated'},
})

const REVIEW_STATUSES: ReviewStatus[] = [
  'PENDING',
  'REVIEWING',
  'APPROVED',
  'REJECTED',
  'ACTIVATED',
]
const LEVELS = Array.from({length: 14}, (_, i) => 85 + i * 5)
const required = '*'
/** El template literal dentro del JSX dispara la regla de i18n; se arma fuera. */
const percent = (level: number) => [level, '%'].join('')

interface Props {
  show: boolean
  onClose: () => void
  onCreated: () => void
}

export function ModalCreateApplication({show, onClose, onCreated}: Props) {
  const intl = useIntl()
  const {data} = useQuery<{onboardingUplineCandidates: IUplineCandidate[]}>(
    GET_ONBOARDING_UPLINE_CANDIDATES,
    {skip: !show}
  )
  const [createApplication, {loading}] = useMutation(CREATE_AGENT_ONBOARDING_APPLICATION)

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    usState: '',
    uplineId: '',
    // The spec defaults this to "Activated"; PENDING is used instead so a manual entry still
    // goes through the same review the portal applications do.
    reviewStatus: 'PENDING' as ReviewStatus,
    commissionLevel: '',
  })

  const set = (field: string, value: string) => setForm((f) => ({...f, [field]: value}))

  const candidates = useMemo(() => data?.onboardingUplineCandidates ?? [], [data])
  const emailError =
    form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())
      ? intl.formatMessage(M.invalidEmail)
      : undefined
  const canSubmit = form.fullName.trim() && form.email.trim() && !emailError

  const uplineOptions = useMemo(
    () =>
      candidates.map((c) => ({
        value: String(c.id),
        label: [c.name, c.email, c.npn ? `NPN ${c.npn}` : null].filter(Boolean).join(' · '),
      })),
    [candidates]
  )
  const levelOptions = LEVELS.map((level) => ({value: String(level), label: percent(level)}))

  const reviewLabel = (status: ReviewStatus) =>
    intl.formatMessage(
      {
        PENDING: M.reviewPENDING,
        REVIEWING: M.reviewREVIEWING,
        APPROVED: M.reviewAPPROVED,
        REJECTED: M.reviewREJECTED,
        ACTIVATED: M.reviewACTIVATED,
      }[status]
    )

  // Se evalúa al render, así que tiene que quedar por debajo de reviewLabel.
  const statusOptions = REVIEW_STATUSES.map((s) => ({value: s, label: reviewLabel(s)}))

  const handleSubmit = async () => {
    try {
      const res = await createApplication({
        variables: {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone || null,
          address: form.address || null,
          dateOfBirth: form.dateOfBirth || null,
          usState: form.usState || null,
          uplineId: form.uplineId || null,
          reviewStatus: form.reviewStatus,
          commissionLevel: form.commissionLevel ? Number(form.commissionLevel) : null,
        },
      })
      const payload = res.data?.createAgentOnboardingApplication
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      toast.success(intl.formatMessage(M.created))
      setForm({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        dateOfBirth: '',
        usState: '',
        uplineId: '',
        reviewStatus: 'PENDING',
        commissionLevel: '',
      })
      onCreated()
      onClose()
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='alert alert-light-primary fs-7'>{intl.formatMessage(M.hint)}</div>

        <div className='row g-4'>
          <div className='col-12'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage(M.fullName)} <span className='text-danger'>{required}</span>
            </label>
            <Form.Control
              placeholder={intl.formatMessage(M.fullNamePh)}
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
            />
          </div>

          <div className='col-md-6'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage(M.email)} <span className='text-danger'>{required}</span>
            </label>
            <Form.Control
              type='email'
              placeholder={intl.formatMessage(M.emailPh)}
              value={form.email}
              isInvalid={!!emailError}
              onChange={(e) => set('email', e.target.value)}
            />
            {emailError && <small className='text-danger'>{emailError}</small>}
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.phone)}</label>
            <PhoneInput
              id='newAgentPhone'
              name='phone'
              value={form.phone}
              placeholder={intl.formatMessage(M.optional)}
              onChange={(_field, value) => set('phone', value)}
            />
          </div>

          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.address)}</label>
            <Form.Control
              placeholder={intl.formatMessage(M.optional)}
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.dob)}</label>
            <Form.Control
              type='date'
              value={form.dateOfBirth}
              onChange={(e) => set('dateOfBirth', e.target.value)}
            />
          </div>

          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.state)}</label>
            <Select
              value={US_STATES.find((s) => s.value === form.usState) ?? null}
              options={US_STATES}
              placeholder={intl.formatMessage(M.statePh)}
              isClearable
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) => set('usState', (opt as {value: string} | null)?.value ?? '')}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.upline)}</label>
            <Select
              value={uplineOptions.find((o) => o.value === form.uplineId) ?? null}
              options={uplineOptions}
              placeholder={intl.formatMessage(M.noUpline)}
              isClearable
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) => set('uplineId', (opt as {value: string} | null)?.value ?? '')}
            />
          </div>

          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.status)}</label>
            <Select
              value={statusOptions.find((o) => o.value === form.reviewStatus) ?? null}
              options={statusOptions}
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) =>
                set('reviewStatus', (opt as {value: string} | null)?.value ?? 'PENDING')
              }
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>{intl.formatMessage(M.commission)}</label>
            <Select
              value={levelOptions.find((o) => o.value === form.commissionLevel) ?? null}
              options={levelOptions}
              placeholder={intl.formatMessage(M.noCommission)}
              isClearable
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) =>
                set('commissionLevel', (opt as {value: string} | null)?.value ?? '')
              }
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose}>
          {intl.formatMessage(M.cancel)}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
        >
          <i className='bi bi-plus-lg me-1' />
          {intl.formatMessage(M.submit)}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
